import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import { format, parseISO, isSameDay } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PrintIcon from "@material-ui/icons/Print";
import CheckIcon from "@material-ui/icons/Check";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import whatsBackground from "../../assets/wa-background.png";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { i18n } from "../../translate/i18n";
import AudioTranscription from "../../components/AudioTranscription";
import {
  scoreColor,
  scoreBadgeLabel,
  verdictColor,
  verdictLabel,
  eventKindColor,
  emotionColor,
  emotionLabel,
  impactColor,
  impactLabel,
  levelLabel,
  vsTeamLabel,
  formatPercent,
} from "../../utils/saleAudit";

// Espelha AuditTicketService.buildTranscript: a IA recebe no máximo as 200
// mensagens mais recentes do ticket, em ordem crescente, numeradas de 0 a n-1.
const TRANSCRIPT_LIMIT = 200;
// Folga sobre o limite: mensagens novas chegadas depois da análise empurram a
// janela auditada para trás, então carregamos um pouco além dela.
const MAX_FETCHED_MESSAGES = TRANSCRIPT_LIMIT + 60;
const MAX_MESSAGE_PAGES = 15;

const HIGHLIGHT_MS = 2000;

const useStyles = makeStyles((theme) => ({
  "@global": {
    "@media print": {
      ".MuiAppBar-root, .MuiDrawer-root, .Toastify": {
        display: "none !important",
      },
      "html, body, #root": {
        height: "auto !important",
        overflow: "visible !important",
      },
    },
  },
  root: {
    padding: theme.spacing(2),
    overflowY: "auto",
    boxSizing: "border-box",
  },
  headerPaper: {
    padding: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 16,
    marginBottom: theme.spacing(2),
  },
  headerMain: {
    flex: "1 1 260px",
    minWidth: 220,
  },
  headerActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  headerScore: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    minWidth: 200,
  },
  scoreValue: {
    fontSize: 52,
    fontWeight: 800,
    lineHeight: 1.1,
  },
  metaGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: "2px 14px",
    marginTop: 6,
  },
  metaItem: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 700,
  },
  conversation: {
    padding: theme.spacing(2),
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto",
    ...theme.scrollbarStyles,
    "@media print": {
      maxHeight: "none",
      overflow: "visible",
    },
  },
  // Fundo/bolhas replicados do MessagesList para a conversa auditada ter a mesma
  // aparência da tela de atendimento (valores copiados, sem acoplar ao componente).
  messagesArea: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    padding: "20px 20px 20px 20px",
    borderRadius: 4,
    marginTop: theme.spacing(1),
    "@media print": {
      // Sem isto o navegador descarta os fundos das bolhas na impressão.
      "&, & *": {
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
      },
    },
  },
  messageRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 2,
    breakInside: "avoid",
    pageBreakInside: "avoid",
  },
  messageMain: {
    flex: "1 1 320px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  bubble: {
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    color: "#303030",
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
    transition: "box-shadow .3s",
  },
  bubbleLeft: {
    backgroundColor: "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  bubbleRight: {
    backgroundColor: "#dcf8c6",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
  },
  // Destaque de evidência: contorno, porque o fundo agora é a textura do chat.
  bubbleHighlight: {
    boxShadow: "0 0 0 3px #2563EB, 0 2px 8px rgba(37, 99, 235, 0.45)",
  },
  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
    fontSize: 13,
    paddingLeft: 6,
  },
  textContentItem: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    padding: "3px 80px 6px 6px",
  },
  mediaTag: {
    display: "block",
    fontSize: 12,
    fontStyle: "italic",
    color: "#667781",
    padding: "2px 6px 0",
  },
  // Bloco de transcrição: fica dentro da bolha mas visualmente separado, para o
  // auditor nunca confundir com texto que o atendente/cliente digitou.
  transcription: {
    margin: "2px 6px 0",
    padding: "4px 6px",
    borderRadius: 4,
    backgroundColor: "rgba(0, 0, 0, 0.035)",
  },
  transcriptionLabel: {
    display: "block",
    fontSize: 11,
    fontStyle: "italic",
    color: "#667781",
    marginBottom: 2,
  },
  transcriptionText: {
    fontSize: 13,
    fontStyle: "italic",
    color: "#3b3b3b",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 110,
    overflowY: "auto",
    paddingRight: 4,
    ...theme.scrollbarStyles,
    "@media print": {
      // Sem isto a transcrição sairia cortada no PDF/impressão.
      maxHeight: "none",
      overflow: "visible",
    },
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },
  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },
  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },
  commentBox: {
    flex: "1 1 220px",
    minWidth: 180,
    borderLeft: "3px solid",
    padding: "4px 8px",
    borderRadius: 4,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  categoryRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "3px 0",
  },
  categoryLabel: {
    width: 130,
    flexShrink: 0,
    fontSize: 13,
  },
  categoryTrack: {
    flex: 1,
    background: "rgba(0,0,0,0.08)",
    borderRadius: 4,
    height: 10,
    overflow: "hidden",
  },
  categoryBar: {
    height: "100%",
    borderRadius: 4,
    minWidth: 2,
    transition: "width .3s",
  },
  categoryScore: {
    width: 30,
    textAlign: "right",
    fontSize: 13,
    fontWeight: 700,
  },
  evidenceBox: {
    margin: "2px 0 10px 8px",
    paddingLeft: 8,
    borderLeft: "2px solid rgba(0,0,0,0.15)",
  },
  benchmarkRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    padding: "3px 0",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  emotionTrack: {
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
    overflowX: "auto",
    padding: "8px 2px 2px",
    ...theme.scrollbarStyles,
  },
  emotionItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 3,
    minWidth: 62,
    flexShrink: 0,
  },
  emotionDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
  },
  emotionLine: {
    flex: 1,
    minWidth: 12,
    height: 2,
    background: "rgba(0,0,0,0.15)",
    marginTop: 5,
  },
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    padding: "3px 0",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 12,
    padding: 32,
  },
  noPrint: {
    "@media print": {
      display: "none !important",
    },
  },
}));

const outlinedChip = (label, color) => (
  <Chip
    size="small"
    variant="outlined"
    label={label}
    style={{ borderColor: color, color, fontWeight: 600 }}
  />
);

// "google/gemini-3-flash-preview" → "gemini-3-flash-preview"
const shortModel = (model) => {
  if (!model) return null;
  const parts = String(model).split("/");
  return parts[parts.length - 1];
};

const formatAnalysisSeconds = (analysisMs) => {
  const n = Number(analysisMs);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${(n / 1000).toFixed(1)}s`;
};

const vsTeamColor = (vsTeam) => {
  if (vsTeam === "acima") return "#0F766E";
  if (vsTeam === "abaixo") return "#E5484D";
  return "#6B7280";
};

const parseDate = (value) => {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (err) {
    return null;
  }
};

const formatMessageTime = (createdAt) => {
  const parsed = parseDate(createdAt);
  return parsed ? format(parsed, "HH:mm") : "";
};

// Tela de auditoria: mídia vira um indicador textual, o arquivo em si não é
// renderizado (o objetivo aqui é ler a conversa, não reproduzi-la).
const MEDIA_LABEL_KEYS = {
  audio: "mediaAudio",
  ptt: "mediaAudio",
  image: "mediaImage",
  sticker: "mediaImage",
  video: "mediaVideo",
  document: "mediaFile",
  application: "mediaFile",
  location: "mediaLocation",
  vcard: "mediaContact",
};

const RAW_PAYLOAD_MEDIA = new Set(["location", "vcard", "multi_vcard"]);

const mediaLabel = (mediaType) => {
  if (!mediaType || mediaType === "chat") return null;
  const key = MEDIA_LABEL_KEYS[mediaType];
  return i18n.t(`saleAuditDetail.conversation.${key || "media"}`);
};

const normalizeText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

// Reforço do casamento por idx: a IA devolve um trecho da mensagem, então se o
// trecho não aparece no corpo da mensagem apontada o vínculo é descartado —
// melhor não mostrar o comentário do que mostrá-lo na mensagem errada.
const excerptMatches = (excerpt, body) => {
  const a = normalizeText(excerpt);
  if (!a) return true;
  const b = normalizeText(body);
  if (!b) return false;
  if (b.includes(a) || a.includes(b)) return true;
  const head = a.slice(0, 24);
  return head.length >= 8 && b.includes(head);
};

const SaleAuditDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const { ticketId } = useParams();

  // Embed (aba do ERP): mesmo padrão de altura das telas irmãs de auditoria.
  const embed = sessionStorage.getItem("whaticketEmbed") === "1";

  const [audit, setAudit] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);

  const messageRefs = useRef({});
  const conversationRef = useRef(null);
  const highlightTimeout = useRef(null);

  useEffect(() => {
    if (!ticketId) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/sale-audits/ticket/${ticketId}`);
        if (active) setAudit(data);
      } catch (err) {
        // 404 = ainda sem auditoria → estado vazio, sem toast.
        if (err?.response?.status !== 404) toastError(err);
        if (active) setAudit(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return undefined;
    let active = true;
    (async () => {
      try {
        let page = 1;
        let all = [];
        let total = 0;
        let more = true;
        while (
          more &&
          all.length < MAX_FETCHED_MESSAGES &&
          page <= MAX_MESSAGE_PAGES
        ) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.get(`/messages/${ticketId}`, {
            params: { pageNumber: page },
          });
          const list = Array.isArray(data?.messages) ? data.messages : [];
          // Cada página vem crescente, mas as páginas andam para trás no tempo.
          all = [...list, ...all];
          if (page === 1) {
            total = Number(data?.count) || list.length;
            if (active) setTicket(data?.ticket || null);
          }
          more = Boolean(data?.hasMore);
          page += 1;
        }
        if (active) {
          setMessages(all);
          setTotalMessages(total);
        }
      } catch (err) {
        if (active) toastError(err);
      }
    })();
    return () => {
      active = false;
    };
  }, [ticketId]);

  useEffect(() => {
    if (!ticketId) return undefined;
    const socket = openSocket();
    socket.on("saleAudit", (data) => {
      if (
        data.action === "update" &&
        Number(data.audit?.ticketId) === Number(ticketId)
      ) {
        setAudit(data.audit);
      }
    });
    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(
    () => () => {
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    },
    [],
  );

  const handleAnalyze = useCallback(async () => {
    if (!ticketId) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(
        `/sale-audits/ticket/${ticketId}/analyze`,
      );
      setAudit(data);
    } catch (err) {
      toastError(err);
    } finally {
      setAnalyzing(false);
    }
  }, [ticketId]);

  // Audits v1 antigos: arrays ausentes → seções escondidas.
  const categories = Array.isArray(audit?.categories) ? audit.categories : [];
  const events = Array.isArray(audit?.events) ? audit.events : [];
  const emotions = Array.isArray(audit?.emotions) ? audit.emotions : [];
  const positives = Array.isArray(audit?.positives) ? audit.positives : [];
  const issues = Array.isArray(audit?.issues) ? audit.issues : [];
  const missedOpportunities = Array.isArray(audit?.missedOpportunities)
    ? audit.missedOpportunities
    : [];
  const coaching = audit?.coaching || null;
  // Amostra pequena → backend manda null e o bloco inteiro some.
  const benchmark = audit?.benchmark || null;
  const benchmarkCategories = Array.isArray(benchmark?.categories)
    ? benchmark.categories
    : [];

  // idx da IA → mensagem carregada. O transcript auditado é a fatia das últimas
  // TRANSCRIPT_LIMIT mensagens que existiam no momento da análise; aqui só temos
  // a cauda da conversa, então converte idx → posição global → posição no array
  // carregado. Fora da janela carregada não há vínculo (melhor nenhum comentário
  // do que um comentário deslocado).
  const messageByIdx = useMemo(() => {
    const map = new Map();
    if (!messages.length) return map;

    const total = totalMessages || messages.length;
    const analyzedAt = audit?.analyzedAt
      ? new Date(audit.analyzedAt).getTime()
      : null;
    const newerThanAudit = analyzedAt
      ? messages.filter(
          (m) => m.createdAt && new Date(m.createdAt).getTime() > analyzedAt,
        ).length
      : 0;

    const totalAtAudit = Math.max(0, total - newerThanAudit);
    const transcriptStart = Math.max(0, totalAtAudit - TRANSCRIPT_LIMIT);
    const loadedStart = total - messages.length;

    for (let idx = 0; idx < totalAtAudit - transcriptStart; idx += 1) {
      const position = transcriptStart + idx - loadedStart;
      if (position >= 0 && position < messages.length) {
        map.set(idx, messages[position]);
      }
    }
    return map;
  }, [messages, totalMessages, audit]);

  const { commentsByMessageId, unmatchedComments } = useMemo(() => {
    const byMessage = new Map();
    const unmatched = [];
    const comments = Array.isArray(audit?.messageComments)
      ? audit.messageComments
      : [];
    comments.forEach((comment, i) => {
      const target =
        comment.idx === null || comment.idx === undefined
          ? null
          : messageByIdx.get(Number(comment.idx));
      if (!target || !excerptMatches(comment.excerpt, target.body)) {
        unmatched.push({ ...comment, key: i });
        return;
      }
      if (!byMessage.has(target.id)) byMessage.set(target.id, comment);
      else unmatched.push({ ...comment, key: i });
    });
    return { commentsByMessageId: byMessage, unmatchedComments: unmatched };
  }, [audit, messageByIdx]);

  const handleShowEvidence = useCallback(
    (evidence) => {
      const target = (Array.isArray(evidence) ? evidence : [])
        .map((ev) =>
          ev.idx === null || ev.idx === undefined
            ? null
            : messageByIdx.get(Number(ev.idx)),
        )
        .find((m) => m);
      if (!target) return;
      const node = messageRefs.current[target.id];
      const painel = conversationRef.current;
      // scrollIntoView rolaria TODOS os ancestrais roláveis. Dentro do iframe
      // do ERP isso arrasta o documento inteiro junto e a área visível cai numa
      // faixa vazia — a conversa "some". Mexendo só no scrollTop do painel, o
      // resto da página fica onde está.
      if (node && painel) {
        // Posição medida na tela, não offsetTop: este depende do offsetParent,
        // que muda conforme o `position` dos ancestrais.
        const alvo =
          painel.scrollTop +
          (node.getBoundingClientRect().top -
            painel.getBoundingClientRect().top) -
          painel.clientHeight / 2 +
          node.clientHeight / 2;
        painel.scrollTo({ top: Math.max(0, alvo), behavior: "smooth" });
      } else if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setHighlightedId(target.id);
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
      highlightTimeout.current = setTimeout(
        () => setHighlightedId(null),
        HIGHLIGHT_MS,
      );
    },
    [messageByIdx],
  );

  const hasEvidenceTarget = useCallback(
    (evidence) =>
      (Array.isArray(evidence) ? evidence : []).some(
        (ev) =>
          ev.idx !== null &&
          ev.idx !== undefined &&
          messageByIdx.has(Number(ev.idx)),
      ),
    [messageByIdx],
  );

  const analysisSeconds = formatAnalysisSeconds(audit?.analysisMs);
  const model = shortModel(audit?.model);
  const contactName =
    ticket?.contact?.name || audit?.ticket?.contact?.name || "—";
  const sellerName = ticket?.user?.name || audit?.user?.name || "—";
  const connectionName = ticket?.whatsapp?.name || "—";

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTimeIcon fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <DoneIcon fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAllIcon fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <DoneAllIcon fontSize="small" className={classes.ackDoneAllIcon} />
      );
    }
    return null;
  };

  const renderDayDivider = (index) => {
    const day = parseDate(messages[index]?.createdAt);
    if (!day) return null;
    if (index > 0) {
      const previousDay = parseDate(messages[index - 1]?.createdAt);
      if (previousDay && isSameDay(day, previousDay)) return null;
    }
    return (
      <span className={classes.dailyTimestamp}>
        <div className={classes.dailyTimestampText}>
          {format(day, "dd/MM/yyyy")}
        </div>
      </span>
    );
  };

  const renderComment = (comment) => (
    <div
      className={classes.commentBox}
      style={{ borderLeftColor: verdictColor(comment.verdict) }}
    >
      <Typography
        variant="caption"
        style={{ color: verdictColor(comment.verdict), fontWeight: 700 }}
        display="block"
      >
        {verdictLabel(comment.verdict)}
      </Typography>
      {comment.note && (
        <Typography variant="body2" style={{ fontSize: 12 }}>
          {comment.note}
        </Typography>
      )}
    </div>
  );

  const renderHeader = () => (
    <Paper variant="outlined" className={classes.headerPaper}>
      <IconButton
        className={classes.noPrint}
        onClick={() => history.push("/sale-audits")}
      >
        <ArrowBackIcon />
      </IconButton>
      <div className={classes.headerMain}>
        <Typography variant="h6">{contactName}</Typography>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("saleAuditDetail.seller")}: {sellerName}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {i18n.t("saleAuditDetail.connection")}: {connectionName}
        </Typography>
      </div>

      <div className={`${classes.headerActions} ${classes.noPrint}`}>
        <Button
          variant="outlined"
          color="primary"
          disabled={analyzing}
          startIcon={
            analyzing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AssessmentOutlinedIcon />
            )
          }
          onClick={handleAnalyze}
        >
          {i18n.t("saleAuditDrawer.buttons.reanalyze")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          {i18n.t("saleAuditDetail.buttons.print")}
        </Button>
      </div>

      {audit && (
        <div className={classes.headerScore}>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("saleAuditDetail.finalScore")}
          </Typography>
          <span
            className={classes.scoreValue}
            style={{ color: scoreColor(audit.score) }}
          >
            {audit.score}
          </span>
          <Chip
            size="small"
            label={scoreBadgeLabel(audit.score)}
            style={{
              backgroundColor: scoreColor(audit.score),
              color: "#fff",
              fontWeight: 600,
            }}
          />
          <div className={classes.metaGrid}>
            <span className={classes.metaItem}>
              {i18n.t("saleAuditDrawer.confidence")}:{" "}
              {formatPercent(audit.confidence)}
            </span>
            {model && (
              <span className={classes.metaItem}>
                {i18n.t("saleAuditDrawer.model")}: {model}
              </span>
            )}
            {analysisSeconds && (
              <span className={classes.metaItem}>
                {i18n.t("saleAuditDrawer.analysisTime")}: {analysisSeconds}
              </span>
            )}
            {audit.analyzedAt && (
              <span className={classes.metaItem}>
                {i18n.t("saleAuditDrawer.analyzedAt")}:{" "}
                {new Date(audit.analyzedAt).toLocaleString("pt-BR")}
              </span>
            )}
          </div>
        </div>
      )}
    </Paper>
  );

  const renderConversation = () => (
    <Paper
      variant="outlined"
      className={classes.conversation}
      ref={conversationRef}
    >
      <Typography
        variant="subtitle2"
        className={classes.sectionTitle}
        gutterBottom
      >
        {i18n.t("saleAuditDetail.conversation.title")}
      </Typography>

      {messages.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {i18n.t("saleAuditDetail.conversation.empty")}
        </Typography>
      )}

      {messages.length > 0 && (
        <div className={classes.messagesArea}>
          {messages.map((message, index) => {
            const comment = commentsByMessageId.get(message.id);
            const isSeller = Boolean(message.fromMe);
            const media = mediaLabel(message.mediaType);
            const isAudioMessage =
              message.mediaType === "audio" || message.mediaType === "ptt";
            // Em location/vcard o `body` é o payload cru do arquivo, não texto
            // que o humano escreveu — só o indicador de mídia faz sentido.
            const body = RAW_PAYLOAD_MEDIA.has(message.mediaType)
              ? ""
              : message.body;
            return (
              <React.Fragment key={message.id}>
                {renderDayDivider(index)}
                <div
                  ref={(node) => {
                    messageRefs.current[message.id] = node;
                  }}
                  className={classes.messageRow}
                >
                  <div className={classes.messageMain}>
                    <div
                      className={`${classes.bubble} ${
                        isSeller ? classes.bubbleRight : classes.bubbleLeft
                      } ${
                        highlightedId === message.id
                          ? classes.bubbleHighlight
                          : ""
                      }`}
                    >
                      <span className={classes.messageContactName}>
                        {isSeller
                          ? i18n.t("saleAuditDrawer.timeline.seller")
                          : i18n.t("saleAuditDrawer.timeline.client")}
                      </span>
                      {media && (
                        <span className={classes.mediaTag}>{media}</span>
                      )}
                      {isAudioMessage && (
                        <AudioTranscription
                          transcription={message.transcription}
                        />
                      )}
                      <div className={classes.textContentItem}>
                        {body}
                        <span className={classes.timestamp}>
                          {formatMessageTime(message.createdAt)}
                          {isSeller && renderMessageAck(message)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {comment && renderComment(comment)}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {unmatchedComments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Typography variant="caption" color="textSecondary" display="block">
            {i18n.t("saleAuditDetail.conversation.unmatched")}
          </Typography>
          {unmatchedComments.map((comment) => (
            <div key={comment.key} style={{ padding: "4px 0" }}>
              {comment.excerpt && (
                <Typography
                  variant="body2"
                  style={{ fontStyle: "italic", fontSize: 13 }}
                >
                  “{comment.excerpt}”
                </Typography>
              )}
              {renderComment(comment)}
            </div>
          ))}
        </div>
      )}
    </Paper>
  );

  const renderCategories = () => (
    <Paper variant="outlined" className={classes.section}>
      <Typography
        variant="subtitle2"
        className={classes.sectionTitle}
        gutterBottom
      >
        {i18n.t("saleAuditDrawer.sections.categories")}
      </Typography>
      {categories.map((cat) => {
        const evidence = Array.isArray(cat.evidence) ? cat.evidence : [];
        return (
          <div key={cat.key}>
            <div className={classes.categoryRow}>
              <span className={classes.categoryLabel}>{cat.label}</span>
              <div className={classes.categoryTrack}>
                <div
                  className={classes.categoryBar}
                  style={{
                    width: `${Math.max(
                      0,
                      Math.min(100, Number(cat.score) || 0),
                    )}%`,
                    background: scoreColor(cat.score),
                  }}
                />
              </div>
              <span
                className={classes.categoryScore}
                style={{ color: scoreColor(cat.score) }}
              >
                {Math.round(Number(cat.score) || 0)}
              </span>
              <span className={classes.metaItem} style={{ width: 46 }}>
                {i18n.t("saleAuditDetail.weight")} {cat.weight ?? 1}
              </span>
            </div>
            {evidence.length > 0 && (
              <div className={classes.evidenceBox}>
                {evidence.map((ev, i) => (
                  <div key={i} style={{ padding: "2px 0" }}>
                    <Typography
                      variant="body2"
                      style={{ fontStyle: "italic", fontSize: 13 }}
                    >
                      “{ev.excerpt}”
                    </Typography>
                    {ev.idx !== null && ev.idx !== undefined && (
                      <Typography variant="caption" color="textSecondary">
                        {i18n.t("saleAuditDrawer.evidence.msgRef")}
                        {ev.idx}
                      </Typography>
                    )}
                  </div>
                ))}
                {hasEvidenceTarget(evidence) && (
                  <Button
                    size="small"
                    color="primary"
                    className={classes.noPrint}
                    startIcon={<VisibilityOutlinedIcon />}
                    onClick={() => handleShowEvidence(evidence)}
                  >
                    {i18n.t("saleAuditDrawer.evidence.show")}
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </Paper>
  );

  const renderBenchmark = () => (
    <Paper variant="outlined" className={classes.section}>
      <Typography
        variant="subtitle2"
        className={classes.sectionTitle}
        gutterBottom
      >
        {i18n.t("saleAuditDrawer.sections.benchmark")}
      </Typography>
      <Typography
        variant="body2"
        style={{ fontWeight: 700, color: scoreColor(benchmark.betterThanPct) }}
      >
        {i18n.t("saleAuditDrawer.benchmark.betterThan", {
          pct: Math.round(Number(benchmark.betterThanPct) || 0),
        })}
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        gutterBottom
      >
        {i18n.t("saleAuditDrawer.benchmark.sampleSize", {
          count: Math.round(Number(benchmark.sampleSize) || 0),
        })}
      </Typography>
      {benchmarkCategories.map((cat) => {
        const delta = Math.round(Number(cat.delta) || 0);
        const color = delta > 0 ? "#0F766E" : delta < 0 ? "#E5484D" : "#6B7280";
        return (
          <div key={cat.key} className={classes.benchmarkRow}>
            <div>
              <Typography variant="body2">{cat.label}</Typography>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("saleAuditDrawer.benchmark.vsTeamAvg", {
                  score: Math.round(Number(cat.score) || 0),
                  teamAvg: Math.round(Number(cat.teamAvg) || 0),
                })}
              </Typography>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color }}>
              {delta > 0 ? "+" : delta < 0 ? "−" : ""}
              {Math.abs(delta)}
            </span>
          </div>
        );
      })}
    </Paper>
  );

  const renderAnalysis = () => (
    <>
      {categories.length > 0 && renderCategories()}
      {benchmark && renderBenchmark()}

      {events.length > 0 && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.events")}
          </Typography>
          <div className={classes.chipsRow}>
            {events.map((event, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={`${event.at} ${event.label}`}
                style={{
                  borderColor: eventKindColor(event.kind),
                  color: eventKindColor(event.kind),
                  fontWeight: 600,
                }}
              />
            ))}
          </div>
        </Paper>
      )}

      {emotions.length > 0 && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.emotions")}
          </Typography>
          <div className={classes.emotionTrack}>
            {emotions.map((point, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className={classes.emotionLine} />}
                <div className={classes.emotionItem}>
                  <div
                    className={classes.emotionDot}
                    style={{ background: emotionColor(point.emotion) }}
                  />
                  <Typography
                    variant="caption"
                    style={{
                      color: emotionColor(point.emotion),
                      fontWeight: 600,
                      lineHeight: 1.1,
                    }}
                  >
                    {emotionLabel(point.emotion)}
                  </Typography>
                  {point.idx !== null && point.idx !== undefined && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ lineHeight: 1 }}
                    >
                      {i18n.t("saleAuditDrawer.evidence.msgRef")}
                      {point.idx}
                    </Typography>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </Paper>
      )}

      {missedOpportunities.length > 0 && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.missed")}
          </Typography>
          {missedOpportunities.map((item, i) => (
            <div key={i} className={classes.listItem}>
              <WarningOutlinedIcon style={{ color: "#EA580C", fontSize: 18 }} />
              <Typography variant="body2">{item}</Typography>
            </div>
          ))}
        </Paper>
      )}

      {positives.length > 0 && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.positives")}
          </Typography>
          {positives.map((item, i) => (
            <div key={i} className={classes.listItem}>
              <CheckIcon style={{ color: "#0F766E", fontSize: 18 }} />
              <Typography variant="body2">{item}</Typography>
            </div>
          ))}
        </Paper>
      )}

      {issues.length > 0 && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.issues")}
          </Typography>
          {issues.map((issue, i) => (
            <div key={i} className={classes.listItem}>
              {outlinedChip(
                impactLabel(issue.impact),
                impactColor(issue.impact),
              )}
              <Typography variant="body2">{issue.label}</Typography>
            </div>
          ))}
        </Paper>
      )}

      {(audit.summary || audit.suggestedAction) && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDetail.opinion")}
          </Typography>
          {audit.summary && (
            <Typography variant="body2" gutterBottom>
              {audit.summary}
            </Typography>
          )}
          {audit.suggestedAction && (
            <>
              <Typography variant="caption" color="textSecondary">
                {i18n.t("saleAuditDrawer.suggestedAction")}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>
                {audit.suggestedAction}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {coaching && (
        <Paper variant="outlined" className={classes.section}>
          <Typography
            variant="subtitle2"
            className={classes.sectionTitle}
            gutterBottom
          >
            {i18n.t("saleAuditDrawer.sections.coaching")}
          </Typography>
          <div className={classes.chipsRow} style={{ marginBottom: 6 }}>
            {(Array.isArray(coaching.train) ? coaching.train : []).map(
              (topic, i) => (
                <Chip
                  key={i}
                  size="small"
                  variant="outlined"
                  label={`${i18n.t("saleAuditDrawer.coaching.train")}: ${topic}`}
                  style={{
                    borderColor: "#2563EB",
                    color: "#2563EB",
                    fontWeight: 600,
                  }}
                />
              ),
            )}
          </div>
          <div className={classes.chipsRow}>
            {coaching.level &&
              outlinedChip(
                `${i18n.t("saleAuditDrawer.coaching.level")}: ${levelLabel(
                  coaching.level,
                )}`,
                "#6B7280",
              )}
            {coaching.vsTeam &&
              outlinedChip(
                `${i18n.t("saleAuditDrawer.coaching.vsTeam")}: ${vsTeamLabel(
                  coaching.vsTeam,
                )}`,
                vsTeamColor(coaching.vsTeam),
              )}
            {coaching.improveProb !== null &&
              coaching.improveProb !== undefined &&
              outlinedChip(
                `${i18n.t(
                  "saleAuditDrawer.coaching.improveProb",
                )}: ${formatPercent(coaching.improveProb)}`,
                "#0F766E",
              )}
          </div>
        </Paper>
      )}
    </>
  );

  return (
    <div
      className={classes.root}
      style={{
        minHeight: embed ? "100vh" : "calc(100vh - 48px)",
        height: embed ? "100vh" : "auto",
      }}
    >
      {loading && (
        <div className={classes.emptyState}>
          <CircularProgress size={28} />
        </div>
      )}

      {!loading && (
        <>
          {renderHeader()}

          {!audit && (
            <Paper variant="outlined" className={classes.emptyState}>
              <AssessmentOutlinedIcon
                style={{ fontSize: 48, color: "#9CA3AF" }}
              />
              <Typography variant="body1">
                {i18n.t("saleAuditDrawer.empty.title")}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {i18n.t("saleAuditDrawer.empty.description")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                className={classes.noPrint}
                disabled={analyzing}
                startIcon={
                  analyzing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
                onClick={handleAnalyze}
              >
                {i18n.t("saleAuditDrawer.buttons.analyze")}
              </Button>
            </Paper>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              {renderConversation()}
            </Grid>
            <Grid item xs={12} md={5}>
              {audit && renderAnalysis()}
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default SaleAuditDetail;
